import React from 'react';
import MarketingHeader from '../components/marketing/MarketingHeader';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { Target, Users, Heart, Award } from 'lucide-react';

const AboutUsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <MarketingHeader />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 dark:from-purple-700 dark:via-pink-700 dark:to-purple-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Fitzaa
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Empowering organizations worldwide to build healthier, happier, and more productive teams through innovative wellness solutions.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Mission
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                To transform workplace wellness by making health and fitness accessible, engaging, and rewarding for every employee.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  What We Do
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Fitzaa is a comprehensive wellness platform designed to help organizations create thriving workplace cultures through engaging health and fitness programs. We combine cutting-edge technology with proven wellness strategies to deliver measurable results.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Our platform enables companies to launch step challenges, team competitions, and holistic wellness initiatives that drive employee engagement, improve health outcomes, and boost organizational performance.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Our Vision</h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        A world where every employee has access to tools and resources that support their physical and mental well-being.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Our Values</h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        We believe in transparency, innovation, and putting people's health and happiness at the center of everything we do.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50 dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Why Choose Fitzaa
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Employee-Focused
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Designed with employees in mind, making wellness accessible and enjoyable for everyone.
                </p>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Proven Results
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Backed by data and trusted by organizations worldwide to deliver measurable wellness outcomes.
                </p>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Comprehensive Platform
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  All-in-one solution for challenges, tracking, analytics, and wellness program management.
                </p>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Secure & Compliant
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Enterprise-grade security with HIPAA, GDPR, and SOC II compliance to protect your data.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Impact
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Helping organizations create healthier workplaces and happier employees
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  1000+
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-lg">
                  Organizations Trust Us
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  50K+
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-lg">
                  Active Users
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  42%
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-lg">
                  Average Activity Increase
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 dark:from-purple-700 dark:via-pink-700 dark:to-purple-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Workplace Wellness?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of organizations using Fitzaa to create healthier, more engaged teams.
            </p>
            <a
              href="/contact-sales?source=about-us"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-purple-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
            >
              Get Started Today
            </a>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
};

export default AboutUsPage;


